import { NavigationSection, OrderStatusDisplay } from '@/types/NavigationTypes'
import { isOrderEditable } from '@/utils/OrderEditability'
import React from 'react'

interface NavigationProps {
  activeSection: NavigationSection
  onNavigationClick: (section: NavigationSection) => void
  hasUnsavedChanges?: boolean
  orderStatus?: OrderStatusDisplay
}

const Navigation: React.FC<NavigationProps> = ({
  activeSection,
  onNavigationClick,
  hasUnsavedChanges = false,
  orderStatus = OrderStatusDisplay.ORDER_PLACED,
}) => {
  // Helper to determine if a status step is complete
  const isStatusComplete = (status: OrderStatusDisplay): boolean => {
    const statusOrder = [
      OrderStatusDisplay.ORDER_PLACED,
      OrderStatusDisplay.ORDER_SUBMITTED,
      OrderStatusDisplay.SAMPLE_RECEIVED,
      OrderStatusDisplay.RESULT_READY,
    ]
    const currentIndex = statusOrder.indexOf(orderStatus)
    const checkIndex = statusOrder.indexOf(status)
    return checkIndex < currentIndex
  }

  // Helper to determine if a status step is current
  const isStatusCurrent = (status: OrderStatusDisplay): boolean => {
    return orderStatus === status
  }

  // Check if results are ready (for showing Lab Report nav)
  const isResultReady = orderStatus === OrderStatusDisplay.RESULT_READY

  // Check if HPO editing is allowed
  const isHPOEditable = isOrderEditable(orderStatus)

  // Get status-specific title and description
  const getStatusContent = () => {
    switch (orderStatus) {
      case OrderStatusDisplay.ORDER_PLACED:
        return {
          title: 'Order Placed',
          description:
            'Your requisition has been placed. You may continue updating HPO until the sample begins processing.',
        }
      case OrderStatusDisplay.ORDER_SUBMITTED:
        return {
          title: 'Order Submitted',
          description:
            'Your requisition has been submitted. You may continue updating HPO until the sample begins processing.',
        }
      case OrderStatusDisplay.SAMPLE_RECEIVED:
        return {
          title: 'Sample Received',
          description: 'HPO updates closed. The lab has started processing the sample.',
        }
      case OrderStatusDisplay.RESULT_READY:
        return {
          title: 'Test Results Available',
          description: 'Your genetic test results are ready for review.',
        }
      default:
        return {
          title: 'Order Submitted',
          description:
            'Your requisition has been submitted. You may continue updating HPO until the sample begins processing.',
        }
    }
  }

  const statusContent = getStatusContent()

  return (
    <div className="left-column">
      <div className="flex flex-col space-y-6">
        {/* Success Indicator */}
        <div className="flex flex-col items-center text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#D3E8BE]">
            <svg className="h-8 w-8 text-green-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="mb-2 mt-4 text-3xl font-bold text-gray-900">{statusContent.title}</h1>
          <p className="body-text mt-2 text-gray-600">{statusContent.description}</p>
        </div>

        {/* Updates Ready to Send - Show when there are unsaved changes */}
        {hasUnsavedChanges && (
          <div className="w-full rounded-lg border border-orange-200 bg-orange-50 p-4">
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <p className="small-text text-orange-800">You have unsaved changes that have not been sent to the lab.</p>
            </div>
          </div>
        )}

        {/* Navigation Links */}
        <div className="w-full">
          <nav className="space-y-2">
            <button
              onClick={() => onNavigationClick('order-summary')}
              className={`relative flex w-full items-center justify-between rounded-md px-3 py-2 transition-colors hover:bg-gray-100 ${
                activeSection === 'order-summary' ? 'bg-[#D3E8BE]' : ''
              }`}
            >
              <span className="page-navigation-title text-gray-700">Order Summary</span>
              <svg className="h-5 w-5 text-green-800" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <button
              onClick={() => onNavigationClick('hpo-terms')}
              className={`relative flex w-full items-center justify-between rounded-md px-3 py-2 transition-colors hover:bg-gray-100 ${
                activeSection === 'hpo-terms' ? 'bg-[#D3E8BE]' : ''
              }`}
            >
              <span className="page-navigation-title text-gray-700">HPO Terms</span>
              {isHPOEditable ? (
                <span className="text-sm font-medium text-green-800 hover:text-green-900 hover:underline">Edit</span>
              ) : (
                <svg className="h-5 w-5 text-green-800" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
            <button
              onClick={() => onNavigationClick('requisition-form')}
              className={`relative flex w-full items-center justify-between rounded-md px-3 py-2 transition-colors hover:bg-gray-100 ${
                activeSection === 'requisition-form' ? 'bg-[#D3E8BE]' : ''
              }`}
            >
              <span className="page-navigation-title text-gray-700">Requisition Form</span>
              <svg className="h-5 w-5 text-green-800" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            {/* Lab Report - Only show when results are ready */}
            {isResultReady && (
              <button
                onClick={() => onNavigationClick('lab-report')}
                className={`relative flex w-full items-center justify-between rounded-md px-3 py-2 transition-colors hover:bg-gray-100 ${
                  activeSection === 'lab-report' ? 'bg-[#D3E8BE]' : ''
                }`}
              >
                <span className="page-navigation-title text-gray-700">Lab Report</span>
                <svg className="h-5 w-5 text-green-800" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
          </nav>
        </div>

        {/* Order Status */}
        <div className="w-full">
          <h2 className="page-navigation-title mb-4">Order Status</h2>
          <div className="relative">
            {/* Connection lines between status items */}
            <div className="absolute bottom-6 left-3 top-6 w-0.5 bg-gray-200"></div>
            <div className="relative space-y-4">
              {/* Order Placed */}
              <div className="relative flex items-center">
                <div
                  className={`relative z-10 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ring-2 ring-white ${
                    isStatusComplete(OrderStatusDisplay.ORDER_PLACED) ||
                    isStatusCurrent(OrderStatusDisplay.ORDER_PLACED)
                      ? 'bg-[#D3E8BE] text-green-800'
                      : 'border-2 border-gray-300 bg-white'
                  }`}
                >
                  {(isStatusComplete(OrderStatusDisplay.ORDER_PLACED) ||
                    isStatusCurrent(OrderStatusDisplay.ORDER_PLACED)) && (
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
                <div className="ml-3">
                  <p
                    className={`small-text font-medium ${
                      isStatusComplete(OrderStatusDisplay.ORDER_PLACED) ||
                      isStatusCurrent(OrderStatusDisplay.ORDER_PLACED)
                        ? 'text-gray-900'
                        : 'text-gray-500'
                    }`}
                  >
                    Order Placed
                  </p>
                </div>
              </div>

              {/* Order Submitted */}
              <div className="relative flex items-center">
                <div
                  className={`relative z-10 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ring-2 ring-white ${
                    isStatusComplete(OrderStatusDisplay.ORDER_SUBMITTED)
                      ? 'bg-[#D3E8BE] text-green-800'
                      : isStatusCurrent(OrderStatusDisplay.ORDER_SUBMITTED)
                        ? 'bg-blue-100 text-blue-800'
                        : 'border-2 border-gray-300 bg-white'
                  }`}
                >
                  {isStatusComplete(OrderStatusDisplay.ORDER_SUBMITTED) && (
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  {isStatusCurrent(OrderStatusDisplay.ORDER_SUBMITTED) && (
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
                <div className="ml-3">
                  <p
                    className={`small-text font-medium ${
                      isStatusComplete(OrderStatusDisplay.ORDER_SUBMITTED) ||
                      isStatusCurrent(OrderStatusDisplay.ORDER_SUBMITTED)
                        ? 'text-gray-900'
                        : 'text-gray-500'
                    }`}
                  >
                    Processing
                  </p>
                </div>
              </div>

              {/* Result Pending */}
              <div className="relative flex items-center">
                <div
                  className={`relative z-10 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ring-2 ring-white ${
                    isStatusComplete(OrderStatusDisplay.SAMPLE_RECEIVED)
                      ? 'bg-[#D3E8BE] text-green-800'
                      : isStatusCurrent(OrderStatusDisplay.SAMPLE_RECEIVED)
                        ? 'bg-blue-100 text-blue-800'
                        : 'border-2 border-gray-300 bg-white'
                  }`}
                >
                  {isStatusComplete(OrderStatusDisplay.SAMPLE_RECEIVED) && (
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  {isStatusCurrent(OrderStatusDisplay.SAMPLE_RECEIVED) && (
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
                <div className="ml-3">
                  <p
                    className={`small-text font-medium ${
                      isStatusComplete(OrderStatusDisplay.SAMPLE_RECEIVED) ||
                      isStatusCurrent(OrderStatusDisplay.SAMPLE_RECEIVED)
                        ? 'text-gray-900'
                        : 'text-gray-500'
                    }`}
                  >
                    Result Pending
                  </p>
                </div>
              </div>

              {/* Result Ready */}
              <div className="relative flex items-center">
                <div
                  className={`relative z-10 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ring-2 ring-white ${
                    isStatusCurrent(OrderStatusDisplay.RESULT_READY)
                      ? 'bg-[#D3E8BE] text-green-800'
                      : 'border-2 border-gray-300 bg-white'
                  }`}
                >
                  {isStatusCurrent(OrderStatusDisplay.RESULT_READY) && (
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
                <div className="ml-3">
                  <p
                    className={`small-text font-medium ${
                      isStatusCurrent(OrderStatusDisplay.RESULT_READY) ? 'text-gray-900' : 'text-gray-500'
                    }`}
                  >
                    Result Ready
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Navigation
